/**
 * Orbit + projection limits for the builder viewer.
 *
 * These numbers are one system, not independent knobs. The backdrop is
 * an origin-centred sky dome (`SceneSky`) drawn on its inside face, so
 * the camera must stay inside it AND the far plane must reach the dome's
 * far wall from wherever the camera is allowed to go. The camera can be
 * displaced from the origin two ways — dollying away from the target,
 * and panning the target itself — so all three terms count:
 *
 *   CAMERA_FAR_M > MAX_TARGET_RADIUS_M
 *                  + MAX_ORBIT_DISTANCE_M
 *                  + SKY_DOME_RADIUS_M
 *
 * Break that and the dome's far half is clipped away. Because the scene
 * has no `scene.background` and the canvas is transparent, the clipped
 * region shows the page's dark CSS gradient through the canvas — the
 * "giant dark ring behind the sauna when zooming out" bug. `far` alone
 * is only the belt: the orbit + pan clamps are the braces, and they are
 * what keep the framing sane (fog fades everything out well before the
 * dome anyway).
 *
 * Forgetting the pan term is exactly how the first fix left the bug
 * reachable: bounding only the dolly still let a right-drag walk the
 * target out to infinity, taking the camera with it.
 *
 * Kept in a standalone module so the invariant can be unit-tested
 * without a WebGL context — see `viewerCameraLimits.test.ts`.
 */

/** Radius of the authored sky dome in `SceneSky`. */
export const SKY_DOME_RADIUS_M = 70;

/**
 * Closest the camera may come to the orbit target. The tightest camera
 * preset (`interior-peek` on the smallest barrel, 1.5 x 1.5 m) sits at
 * ~2.12 m, so this leaves headroom below every preset while still
 * stopping the user from pushing the near plane through a wall.
 */
export const MIN_ORBIT_DISTANCE_M = 1.5;

/**
 * Furthest the camera may pull back. The widest preset (`hero` on a
 * 4 x 6 m fyrkantig — already past the 4 x 5 m slider ceiling) sits at
 * ~11.32 m, so 26 m is well over 2x the framing distance in every
 * configuration and still far inside the 70 m dome.
 */
export const MAX_ORBIT_DISTANCE_M = 26;

/**
 * Lowest the camera may swing, measured from straight up. Presets top
 * out at ~81.4° (`side` on a two-room), so 87° keeps every preset off
 * the clamp while stopping the user from orbiting under the ground
 * plane and looking up through the terrain.
 */
export const MAX_ORBIT_POLAR_ANGLE_RAD = (87 * Math.PI) / 180;

/**
 * How far the orbit target itself may be panned from the origin, as a
 * true 3D radius. Panning stays enabled — it is genuinely useful for
 * inspecting a corner or a foundation pier — but an unbounded target
 * drags the camera out of the dome just as effectively as dollying
 * does. Every camera preset's target sits within ~2.55 m of the origin,
 * so 8 m is roughly 3x the framing envelope and still leaves the
 * three-term invariant 6 m of headroom (8 + 26 + 70 = 104 < 110).
 *
 * Being a 3D bound, it also caps how high the target can be lifted.
 */
export const MAX_TARGET_RADIUS_M = 8;

/**
 * Floor for the orbit target's height. Pan is the one gesture that can
 * bypass `MAX_ORBIT_POLAR_ANGLE_RAD` — drop the target below grade and
 * the camera follows it underground, looking up through the terrain.
 *
 * Grade level (0) is the floor rather than the actual ground surface:
 * `getGroundBaseY` puts the terrain at −0.55 m by default (and near
 * −1 m on 900 mm plinths), so a target at 0 keeps the camera above the
 * ground everywhere inside the pan radius on flat and mildly sloped
 * sites. Fully cranked slope sliders (±20 % on both axes) can still lift
 * the terrain above the camera at the far edge of the pan radius; that
 * is a deliberate non-goal here — this floor exists to stop the
 * under-the-world view, not to collide the camera with the mesh.
 */
export const MIN_TARGET_Y_M = 0;

/** Perspective near plane for the viewer canvas. */
export const CAMERA_NEAR_M = 0.1;

/**
 * Perspective far plane. Must satisfy the invariant at the top of this
 * file: 8 + 26 + 70 = 104 m of dome to cover, plus margin.
 */
export const CAMERA_FAR_M = 110;

/** The mutable subset of `THREE.Vector3` that {@link clampOrbitTarget} needs. */
export interface MutableVec3 {
	x: number;
	y: number;
	z: number;
}

/**
 * Slack on the radius bound, and the reason it exists.
 *
 * `scale = MAX / radius` followed by three multiplications does not land
 * exactly on the boundary sphere: for about a fifth of all inputs the
 * result is one or two ulp ABOVE it (measured worst case at this
 * magnitude: 8.000000000000004, i.e. +3.6e-15). Comparing against the
 * bare bound would therefore re-scale on the next pass, report "moved"
 * forever, and leave convergence resting on three's own `_EPS` guard for
 * dispatching 'change' — a guard in a different module, for a different
 * purpose. One nanometre of tolerance makes the fixed point real and
 * owned here. Against a 6 m invariant margin the residual is nothing.
 */
export const TARGET_RADIUS_TOLERANCE_M = 1e-9;

/**
 * Clamp an OrbitControls target back inside the pan bounds, in place.
 *
 * Returns `true` when it actually moved the target, so the caller can
 * repaint the demand frameloop only when something changed.
 *
 * The height floor is applied first, and the radius clamp then scales
 * the whole vector toward the origin — which can only raise a negative
 * y toward zero, never push it back below the floor. One pass therefore
 * satisfies both bounds, and (thanks to the tolerance above) a second
 * pass is a bit-exact no-op that returns `false`, so the 'change'
 * handler calling this cannot ping-pong.
 *
 * Why this and not three's native `maxTargetRadius`, which clamps inside
 * `update()` before the camera is positioned: drei's `<OrbitControls>`
 * instantiates **three-stdlib**'s fork, not `three/examples/jsm`, and
 * three-stdlib 2.36.1 has no `maxTargetRadius`/`cursor` at all. The prop
 * would typecheck against `@types/three` and do nothing — a silent
 * no-op dressed as a fix. Revisit if drei ever moves off three-stdlib.
 */
export function clampOrbitTarget(target: MutableVec3): boolean {
	const originalX = target.x;
	const originalY = target.y;
	const originalZ = target.z;

	if (target.y < MIN_TARGET_Y_M) {
		target.y = MIN_TARGET_Y_M;
	}

	const radius = Math.hypot(target.x, target.y, target.z);
	if (radius > MAX_TARGET_RADIUS_M + TARGET_RADIUS_TOLERANCE_M) {
		const scale = MAX_TARGET_RADIUS_M / radius;
		target.x *= scale;
		target.y *= scale;
		target.z *= scale;
	}

	return (
		target.x !== originalX || target.y !== originalY || target.z !== originalZ
	);
}
